/**
 * Knockout bracket assembly for a generic tournament (specs/champions-league, Story 6).
 *
 * Round ORDER comes from the competition's own `stageOrder`, never from a World-Cup-specific
 * seed constant (`WORLD_CUP_R32_SLOT_SEEDS`), so the next tournament declares its own shape
 * in config rather than in code.
 *
 * Two-legged ties are paired by the UNORDERED team pair: the second leg swaps home and away,
 * so `{home, away}` as a set is the only signal that survives. Aggregate score is deliberately
 * not computed — each leg keeps its own independent prediction (spec §Non-Goals).
 *
 * Pure — no React.
 */

import type { StageSectionMatchLike } from './tournamentStageSections.helper';

export interface BracketMatchLike extends StageSectionMatchLike {
  homeTeamId: string;
  awayTeamId: string;
  kickoffTime: string;
}

/** Unordered pair key, so a home-and-away tie maps to one slot. */
export function tiePairKey(homeTeamId: string, awayTeamId: string): string {
  return [homeTeamId, awayTeamId].sort().join('~');
}

/**
 * Stages that belong in a bracket: everything in the competition's stage order EXCEPT its
 * multi-round stages, which are the table phase (a league phase or a group stage), not a
 * knockout round.
 */
export function bracketStages(
  stageOrder: readonly string[],
  multiRoundStages: Record<string, readonly string[]>,
): string[] {
  return stageOrder.filter((stage) => {
    const rounds = multiRoundStages?.[stage];
    return !(Array.isArray(rounds) && rounds.length > 0);
  });
}
